import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Channel as PhxChannel, Socket } from 'phoenix'
import { PcmStreamPlayer, getSharedAudioContext, unlockPcmAudio } from 'lib/audio/OpusStreamPlayer'
import { fireError, fireWarning } from 'models/PopUpNotifications'
import { createOpusDecoder, createWebCodecsOpusEncoder } from 'lib/audio/opusCodec'
import type { DecoderWrapper, OpusWebCodecs } from 'lib/audio/opusCodec'
import type { VoiceClientEvent, VoiceClientState } from 'hooks/voice/types'
import {
  DisplayIndex,
  FleetDisplay,
  HeldStream,
  SeqLedger,
  diffHeld,
  indexDisplay,
  parseFrame,
  parseSteer,
  resolveSteerUrl,
} from './protocol'

const NOOP = (_?: unknown) => {
  /* intentionally empty */
}

const DEBUG_AUDIO = true
const flog = (...a: any[]) => {
  if (DEBUG_AUDIO) console.log('[Fleet]', ...a)
}

const normalizeNames = (names: string[]) => Array.from(new Set(names.map(n => String(n || '').trim()).filter(Boolean)))

// Frames that arrive before the display naming their sender's floor.
const PENDING_FRAME_CAP = 25
// Consecutive socket errors before the token is assumed stale and refetched.
const AUTH_TROUBLE_ERRORS = 2

export type UseFleetOpts = {
  // The org's `voice.url` (`wss://…/device`); null keeps the client idle.
  url: string | null
  // Read on every (re)connect, so a refreshed token is picked up.
  getToken: () => string | null
  // Our own subject id: frames we sent are never played back.
  subjectId: string | null
  language?: string
  // Called when the socket keeps failing to open, e.g. an expired token.
  onAuthTrouble?: () => void
}

type RxStream = {
  streamId: number
  held: HeldStream
  player: PcmStreamPlayer | null
  decoder: DecoderWrapper | null
  ready: boolean
  pending: Uint8Array[]
  decoded: number
}

type PushResult = { ok: boolean; response: any }

// One socket and its `client` channel. `steered` marks a socket opened at a
// steer's `home`, which may name a single task.
type Conn = { url: string; socket: Socket; channel: PhxChannel; steered: boolean }

type PendingSteer = { from: Conn; to: Conn; timer: ReturnType<typeof setTimeout> }

function pushAsync(channel: PhxChannel, event: string, payload: object | ArrayBuffer): Promise<PushResult> {
  return new Promise(resolve => {
    channel
      .push(event, payload as object)
      .receive('ok', response => resolve({ ok: true, response }))
      .receive('error', response => resolve({ ok: false, response }))
      .receive('timeout', () => resolve({ ok: false, response: { reason: 'timeout' } }))
  })
}

// Fleet transport: one socket and one `client` channel carry every
// membership, the server owns memberships, and a channel is talked on by
// `select` + `press`. `hooks/voice/adapters/useFleetAdapter` exposes it to
// VoiceProvider as a `VoiceClient`.
export function useFleet({ url, getToken, subjectId, language = 'en', onAuthTrouble }: UseFleetOpts) {
  const [state, setState] = useState<VoiceClientState>({
    status: 'idle',
    rx: 'idle',
    tx: 'idle',
    sdkReady: true,
    readyChannels: [],
  })

  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const onAuthTroubleRef = useRef(onAuthTrouble)
  onAuthTroubleRef.current = onAuthTrouble
  const subjectIdRef = useRef(subjectId)
  subjectIdRef.current = subjectId

  // The connection serving the session, and the one a steer is opening.
  const connRef = useRef<Conn | null>(null)
  const steerRef = useRef<PendingSteer | null>(null)
  const reopenRef = useRef<() => void>(NOOP)
  const kickedRef = useRef(false)
  const socketErrorsRef = useRef(0)
  const desiredRef = useRef<string[]>([])

  const indexRef = useRef<DisplayIndex | null>(null)
  const selectedRef = useRef<string | null>(null)
  const activeTxChannelRef = useRef<string | null>(null)

  const emitRef = useRef<((e: VoiceClientEvent) => void) | null>(null)
  const setOnEvent = useCallback((fn: ((e: VoiceClientEvent) => void) | null) => {
    emitRef.current = fn
  }, [])
  const emit = useCallback((e: VoiceClientEvent) => {
    emitRef.current?.(e)
  }, [])

  // ===== RX =====

  const enabledRxRef = useRef<Set<string>>(new Set())
  const streamsRef = useRef<Map<number, RxStream>>(new Map())
  const streamBySenderRef = useRef<Map<string, number>>(new Map())
  const pendingBySenderRef = useRef<Map<string, Uint8Array[]>>(new Map())
  const nextStreamIdRef = useRef(1)
  const ledgerRef = useRef(new SeqLedger())
  const lastPlayerRef = useRef<PcmStreamPlayer | null>(null)
  const seenSendersRef = useRef<Set<string>>(new Set())

  const playOpus = useCallback((stream: RxStream, opus: Uint8Array) => {
    if (!stream.ready || !stream.decoder || !stream.player) {
      if (stream.pending.length < PENDING_FRAME_CAP * 8) stream.pending.push(opus)
      return
    }
    try {
      const res = stream.decoder.decode(opus)
      const chans = res?.channelData
      if (stream.decoded++ === 0) {
        flog('first decode', {
          streamId: stream.streamId,
          bytes: opus.length,
          samples: res?.samplesDecoded ?? chans?.[0]?.length,
          errors: res?.errors?.length ?? 0,
          ctxState: stream.player.audioContext?.state,
        })
      }
      if (chans?.length && enabledRxRef.current.has(stream.held.channelName)) stream.player.enqueue(chans)
    } catch (err) {
      flog('decode error', { streamId: stream.streamId, bytes: opus.length, err: String((err as any)?.message || err) })
    }
  }, [])

  const stopStream = useCallback(
    (streamId: number) => {
      const stream = streamsRef.current.get(streamId)
      if (!stream) return
      try {
        stream.player?.stop()
      } catch (err) {
        NOOP(err)
      }
      streamsRef.current.delete(streamId)
      if (streamBySenderRef.current.get(stream.held.senderId) === streamId) {
        streamBySenderRef.current.delete(stream.held.senderId)
      }
      emit({ type: 'stream_stop', stream_id: streamId, channel: stream.held.channelName })
    },
    [emit],
  )

  const startStream = useCallback(
    (held: HeldStream) => {
      const streamId = nextStreamIdRef.current++
      const stream: RxStream = { streamId, held, player: null, decoder: null, ready: false, pending: [], decoded: 0 }
      streamsRef.current.set(streamId, stream)
      streamBySenderRef.current.set(held.senderId, streamId)

      const early = pendingBySenderRef.current.get(held.senderId)
      if (early) {
        stream.pending.push(...early)
        pendingBySenderRef.current.delete(held.senderId)
      }

      const init = async () => {
        stream.player = new PcmStreamPlayer({ sampleRate: 48000, numChannels: 1 })
        flog('rx stream starting', {
          streamId,
          ctxState: getSharedAudioContext()?.state,
          pending: stream.pending.length,
        })
        await stream.player.start()
        lastPlayerRef.current = stream.player
        stream.decoder = await createOpusDecoder(stream.player.sampleRate, 1)
        stream.ready = true
        flog('rx stream ready', {
          streamId,
          ctxState: stream.player.audioContext?.state,
          sampleRate: stream.player.sampleRate,
          pending: stream.pending.length,
        })
        for (const opus of stream.pending.splice(0)) playOpus(stream, opus)
      }
      init().catch(err => console.warn('[Fleet] rx stream init failed', err))

      emit({ type: 'stream_start', stream_id: streamId, channel: held.channelName, from: held.senderId })
    },
    [emit, playOpus],
  )

  const syncRxState = useCallback(() => {
    const receiving = streamsRef.current.size > 0
    setState(p => (p.rx === (receiving ? 'receiving' : 'idle') ? p : { ...p, rx: receiving ? 'receiving' : 'idle' }))
  }, [])

  const stopAllRx = useCallback(() => {
    Array.from(streamsRef.current.keys()).forEach(stopStream)
    pendingBySenderRef.current.clear()
    ledgerRef.current.clear()
    setState(p => ({ ...p, rx: 'idle' }))
  }, [stopStream])

  const handleFrame = useCallback(
    (payload: ArrayBuffer) => {
      const frame = parseFrame(payload)
      if (!frame) return
      if (frame.senderId === subjectIdRef.current) return
      if (!ledgerRef.current.accept(frame.senderId, frame.seq)) return

      const streamId = streamBySenderRef.current.get(frame.senderId)
      const stream = streamId != null ? streamsRef.current.get(streamId) : undefined
      if (!seenSendersRef.current.has(frame.senderId)) {
        seenSendersRef.current.add(frame.senderId)
        flog('first frame', {
          sender: frame.senderId,
          bytes: frame.opus.length,
          seq: frame.seq,
          streamId: streamId ?? null,
        })
      }
      if (stream) {
        playOpus(stream, frame.opus)
        return
      }
      const early = pendingBySenderRef.current.get(frame.senderId) || []
      if (early.length < PENDING_FRAME_CAP) early.push(frame.opus)
      pendingBySenderRef.current.set(frame.senderId, early)
    },
    [playOpus],
  )

  const isChannelRxEnabled = useCallback((channel: string | null | undefined) => {
    const name = String(channel || '').trim()
    return !!name && enabledRxRef.current.has(name)
  }, [])

  const setChannelRxEnabled = useCallback(
    (channel: string, enabled: boolean) => {
      const name = String(channel || '').trim()
      if (!name) return
      const next = new Set(enabledRxRef.current)
      if (enabled) next.add(name)
      else next.delete(name)
      enabledRxRef.current = next
      if (!enabled) {
        streamsRef.current.forEach(s => {
          if (s.held.channelName === name) stopStream(s.streamId)
        })
        syncRxState()
      }
    },
    [stopStream, syncRxState],
  )

  const toggleChannelRxEnabled = useCallback(
    (channel: string) => setChannelRxEnabled(channel, !enabledRxRef.current.has(String(channel || '').trim())),
    [setChannelRxEnabled],
  )

  const setRxEnabledChannels = useCallback((channels: string[]) => {
    enabledRxRef.current = new Set(normalizeNames(channels))
  }, [])

  const getRxEnabledChannels = useCallback(() => Array.from(enabledRxRef.current.values()), [])

  const resumeAudio = useCallback(async () => {
    await lastPlayerRef.current?.resume()
  }, [])

  const setStreamVolume = useCallback((streamId: number, volume: number) => {
    try {
      streamsRef.current.get(Number(streamId))?.player?.setVolume(volume)
    } catch (err) {
      NOOP(err)
    }
  }, [])

  // ===== TX =====

  const txRef = useRef<{
    encoder: OpusWebCodecs
    media: MediaStream
    src: MediaStreamAudioSourceNode
    proc: ScriptProcessorNode
    zero: GainNode
  } | null>(null)
  const txActiveRef = useRef(false)
  const startGenRef = useRef(0)

  const teardownCapture = useCallback(async () => {
    const tx = txRef.current
    txRef.current = null
    if (!tx) return
    try {
      tx.proc.disconnect()
    } catch (err) {
      NOOP(err)
    }
    try {
      tx.zero.disconnect()
    } catch (err) {
      NOOP(err)
    }
    try {
      tx.src.disconnect()
    } catch (err) {
      NOOP(err)
    }
    tx.media.getTracks().forEach(t => t.stop())
    try {
      await tx.encoder.close()
    } catch (err) {
      NOOP(err)
    }
  }, [])

  const stopTalking = useCallback(
    async (_channelHint?: string) => {
      startGenRef.current++
      const wasActive = txActiveRef.current
      txActiveRef.current = false
      await teardownCapture()
      const channel = connRef.current?.channel
      if (wasActive && channel) void pushAsync(channel, 'release', {})
      setState(p => (p.tx === 'idle' ? p : { ...p, tx: 'idle' }))
    },
    [teardownCapture],
  )

  // Mic → 20 ms frames → WebCodecs Opus → one binary `frame` push each.
  const startCapture = useCallback(async (channel: PhxChannel, isStale: () => boolean) => {
    const ctx = getSharedAudioContext()
    if (!ctx) throw new Error('AudioContext is not supported in this browser')
    if (ctx.state !== 'running') {
      try {
        await ctx.resume()
      } catch (err) {
        NOOP(err)
      }
    }
    if (ctx.state !== 'running') throw new Error('Audio is locked. Click anywhere on the page first, then try again.')

    const AudioDataCtor = (window as any).AudioData
    if (!AudioDataCtor) throw new Error('WebCodecs AudioData is not available in this browser.')

    const sampleRate = ctx.sampleRate
    const encoder = await createWebCodecsOpusEncoder({
      sampleRate,
      numberOfChannels: 1,
      bitrate: 24000,
      onOpus: opus => {
        if (!txActiveRef.current) return
        channel.push('frame', opus.buffer.slice(opus.byteOffset, opus.byteOffset + opus.byteLength) as any)
      },
    })

    let media: MediaStream | null = null
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (isStale()) throw new Error('cancelled')
    } catch (err) {
      media?.getTracks().forEach(t => t.stop())
      await encoder.close()
      throw err
    }

    const src = ctx.createMediaStreamSource(media)
    const proc = ctx.createScriptProcessor(1024, 1, 1)
    const zero = ctx.createGain()
    zero.gain.value = 0
    src.connect(proc)
    proc.connect(zero)
    zero.connect(ctx.destination)
    txRef.current = { encoder, media, src, proc, zero }

    const frameSamples = Math.round(0.02 * sampleRate)
    const ring = new Float32Array(frameSamples * 10)
    let ringLen = 0
    let timestampUs = 0

    proc.onaudioprocess = ev => {
      if (!txActiveRef.current) return
      const input = ev.inputBuffer.getChannelData(0)
      if (ringLen + input.length > ring.length) ringLen = 0
      ring.set(input, ringLen)
      ringLen += input.length

      while (ringLen >= frameSamples) {
        const pcm = ring.slice(0, frameSamples)
        ring.copyWithin(0, frameSamples, ringLen)
        ringLen -= frameSamples
        try {
          const data = new AudioDataCtor({
            format: 'f32',
            sampleRate,
            numberOfFrames: frameSamples,
            numberOfChannels: 1,
            timestamp: timestampUs,
            data: pcm,
          })
          timestampUs += Math.round((frameSamples / sampleRate) * 1_000_000)
          encoder.encoder.encode(data)
          data.close()
        } catch (err) {
          NOOP(err)
          break
        }
      }
    }
  }, [])

  const startTalking = useCallback(
    async (channelName?: string) => {
      const name = (channelName || activeTxChannelRef.current || '').trim()
      const channel = connRef.current?.channel
      if (!name) {
        fireWarning({ content: 'No channel selected to talk on.' })
        return
      }
      if (!channel || !indexRef.current) {
        fireWarning({ content: 'Not connected to the voice service. Please wait and try again.' })
        return
      }
      if (txActiveRef.current) return

      const channelId = indexRef.current.idByName.get(name.toLowerCase())
      if (!channelId) {
        setState(p => ({ ...p, error: 'channel not available', lastEvent: 'no_channel' }))
        fireWarning({ content: `You are not a member of "${name}" yet. Try again in a minute.` })
        return
      }

      void unlockPcmAudio()
      const gen = ++startGenRef.current
      const isStale = () => startGenRef.current !== gen
      setState(p => ({ ...p, tx: 'sending', error: undefined }))

      if (selectedRef.current !== channelId) {
        const selected = await pushAsync(channel, 'select', { channel: channelId })
        if (isStale()) return
        if (!selected.ok) {
          const reason = String(selected.response?.reason || 'select failed')
          setState(p => ({ ...p, tx: 'idle', error: reason, lastEvent: 'select_error' }))
          fireError({ content: `Could not select "${name}": ${reason}` })
          return
        }
        selectedRef.current = channelId
      }

      let pressed = await pushAsync(channel, 'press', {})
      if (!pressed.ok && pressed.response?.reason === 'attaching') {
        await new Promise(r => setTimeout(r, 300))
        if (isStale()) return
        pressed = await pushAsync(channel, 'press', {})
      }
      if (isStale()) {
        if (pressed.ok) void pushAsync(channel, 'release', {})
        return
      }
      if (!pressed.ok) {
        const reason = String(pressed.response?.reason || 'press failed')
        // VoiceProvider treats an error containing "busy" as the busy bounce.
        const error = reason === 'held' ? 'channel busy' : reason
        setState(p => ({ ...p, tx: 'idle', error, lastEvent: 'press_error' }))
        if (reason === 'held') fireError({ content: 'Channel is busy. Try again shortly.' })
        else fireError({ content: `Could not start talking: ${reason}` })
        return
      }

      txActiveRef.current = true
      try {
        await startCapture(channel, isStale)
        if (isStale()) throw new Error('cancelled')
      } catch (err: any) {
        txActiveRef.current = false
        await teardownCapture()
        void pushAsync(channel, 'release', {})
        if (String(err?.message) !== 'cancelled') {
          const errTxt = String(err?.message || err || 'Microphone could not be started.')
          setState(p => ({ ...p, tx: 'idle', error: errTxt, lastEvent: 'mic_error' }))
          fireError({ content: errTxt })
        } else {
          setState(p => ({ ...p, tx: 'idle' }))
        }
      }
    },
    [startCapture, teardownCapture],
  )

  // ===== Display =====

  const applyDisplay = useCallback(
    (display: FleetDisplay) => {
      const prev = indexRef.current
      const next = indexDisplay(display)
      indexRef.current = next
      selectedRef.current = display.selected

      const { started, stopped } = diffHeld(prev?.held || new Map(), next.held)
      stopped.forEach(s => {
        const streamId = streamBySenderRef.current.get(s.senderId)
        if (streamId != null) stopStream(streamId)
      })
      started.forEach(s => {
        const self = s.senderId === subjectIdRef.current
        const enabled = enabledRxRef.current.has(s.channelName)
        flog('floor taken', {
          channel: s.channelName,
          sender: s.senderId,
          self,
          enabled,
          listening: Array.from(enabledRxRef.current),
        })
        if (self || !enabled) return
        startStream(s)
      })

      // The server ended our hold: the 5 s talk cap, or a lost tiebreak.
      if (txActiveRef.current && !display.talking) {
        const reason = display.ended?.reason
        void stopTalking()
        if (reason === 'max_talk') fireWarning({ content: 'Talk time limit reached. Press again to keep talking.' })
        if (reason === 'revoked') fireWarning({ content: 'Someone else took the channel.' })
      }

      const receiving = streamsRef.current.size > 0
      setState(p => ({
        ...p,
        status: 'connected',
        error: p.status === 'connected' ? p.error : undefined,
        rx: receiving ? 'receiving' : 'idle',
        readyChannels: next.readyNames,
        connectionFailed: false,
        lastEvent: 'display',
      }))
    },
    [startStream, stopStream, stopTalking],
  )

  const handleTranscript = useCallback(
    (msg: any, translated: boolean) => {
      if (translated && String(msg?.target_language || '').slice(0, 2) !== language.slice(0, 2)) return
      const channelName = indexRef.current?.nameById.get(String(msg?.channel || ''))
      if (!channelName || !enabledRxRef.current.has(channelName)) return
      const streamId = streamBySenderRef.current.get(String(msg?.sender_id || '')) ?? 0
      const text = String(msg?.text || '')
      emit({
        type: 'transcription',
        stream_id: streamId,
        channel: channelName,
        from: msg?.sender_id,
        text,
        language: translated ? msg?.target_language : msg?.language,
        // VoiceProvider speaks a translation matching the listener's language.
        translations: translated ? [{ language: msg?.target_language, message: text }] : undefined,
      })
    },
    [emit, language],
  )

  // ===== Connection =====

  const disposeConn = (conn: Conn | null | undefined) => {
    if (!conn) return
    try {
      conn.channel.leave()
    } catch (err) {
      NOOP(err)
    }
    try {
      conn.socket.disconnect()
    } catch (err) {
      NOOP(err)
    }
  }

  // Drops a steer in flight and closes the socket it was opening. The old
  // socket keeps serving until the server closes it at `ttl_ms`, and the
  // ordinary reconnect then lands on the home through the load balancer.
  const abandonSteer = useCallback((reason: string) => {
    const steer = steerRef.current
    if (!steer) return
    steerRef.current = null
    clearTimeout(steer.timer)
    flog('steer abandoned', reason)
    disposeConn(steer.to)
  }, [])

  const closeSocket = useCallback(() => {
    txActiveRef.current = false
    void teardownCapture()
    stopAllRx()
    abandonSteer('closed')
    disposeConn(connRef.current)
    connRef.current = null
    indexRef.current = null
    selectedRef.current = null
  }, [abandonSteer, stopAllRx, teardownCapture])

  // Builds, joins and connects one socket. Its handlers act only while it is
  // the current connection or the one a steer is opening, so a vacated
  // socket's late events never touch the session.
  const buildConnection = useCallback(
    (target: string, slip?: string): Conn => {
      // fleet's docs/voice-steering.md: the slip rides on this connect only.
      let firstSlip = slip
      const socket = new Socket(target, {
        params: () => {
          const token = getTokenRef.current() || ''
          return firstSlip ? { token, slip: firstSlip } : { token }
        },
      })
      const channel = socket.channel('client', () => ({ language }))
      const conn: Conn = { url: target, socket, channel, steered: !!slip }
      const isCurrent = () => connRef.current === conn
      const isSteerTarget = () => steerRef.current?.to === conn

      socket.onOpen(() => {
        firstSlip = undefined
        if (isCurrent()) socketErrorsRef.current = 0
      })
      socket.onError(() => {
        if (isSteerTarget()) return abandonSteer('connect failed')
        if (!isCurrent() || kickedRef.current) return
        // A steered socket may name a task that is gone; start over at the
        // org's own URL, where the pin finds the home.
        if (conn.steered) return reopenRef.current()
        socketErrorsRef.current += 1
        if (socketErrorsRef.current === AUTH_TROUBLE_ERRORS) onAuthTroubleRef.current?.()
        setState(p =>
          p.status === 'lost' ? p : { ...p, status: 'lost', readyChannels: [], lastEvent: 'socket_error' },
        )
      })

      channel.on('display', (display: FleetDisplay) => {
        if (isCurrent()) return applyDisplay(display)
        if (!isSteerTarget()) return
        // The new socket holds the session: make it current and close the
        // old one ourselves (1000), so phoenix.js does not reconnect it.
        const steer = steerRef.current as PendingSteer
        steerRef.current = null
        clearTimeout(steer.timer)
        connRef.current = conn
        flog('steer complete', target)
        applyDisplay(display)
        disposeConn(steer.from)
      })
      // Both sockets hear frames while a steer is in flight; the ledger
      // drops the second copy.
      channel.on('frame', (payload: ArrayBuffer) => {
        if (isCurrent() || isSteerTarget()) handleFrame(payload)
      })
      channel.on('transcript', msg => {
        if (isCurrent()) handleTranscript(msg, false)
      })
      channel.on('translation', msg => {
        if (isCurrent()) handleTranscript(msg, true)
      })
      channel.on('steer', payload => {
        if (!isCurrent() || steerRef.current || kickedRef.current) return
        const steer = parseSteer(payload)
        const next = steer && resolveSteerUrl(steer.home, conn.url)
        if (!steer || !next) return
        flog('steer', { home: steer.home, target: next, ttl_ms: steer.ttl_ms })
        // A hold is released on the socket being vacated.
        if (txActiveRef.current) void stopTalking()
        const to = buildConnection(next, steer.slip)
        const timer = setTimeout(() => abandonSteer('ttl'), steer.ttl_ms)
        steerRef.current = { from: conn, to, timer }
      })
      channel.on('superseded', () => {
        if (!isCurrent()) return
        // Expected: our own steer moved the session to the new socket.
        if (steerRef.current?.from === conn) return
        // The same user connected elsewhere; reconnecting would just take the
        // session back and forth. Wait for an explicit Reconnect.
        kickedRef.current = true
        closeSocket()
        setState(p => ({ ...p, status: 'kicked', tx: 'idle', error: 'kicked', lastEvent: 'superseded' }))
      })
      channel.onError(() => {
        if (!isCurrent() || kickedRef.current) return
        stopAllRx()
        indexRef.current = null
      })
      channel.join().receive('error', resp => {
        if (isSteerTarget()) return abandonSteer('join failed')
        if (!isCurrent()) return
        setState(p => ({ ...p, error: String(resp?.reason || 'join failed'), lastEvent: 'join_error' }))
      })

      socket.connect()
      return conn
    },
    [abandonSteer, applyDisplay, closeSocket, handleFrame, handleTranscript, language, stopAllRx, stopTalking],
  )

  const openSocket = useCallback(() => {
    if (!url) return
    kickedRef.current = false
    socketErrorsRef.current = 0
    setState(p => ({ ...p, status: 'connecting', readyChannels: [], error: undefined }))
    connRef.current = buildConnection(url)
  }, [buildConnection, url])

  reopenRef.current = () => {
    closeSocket()
    openSocket()
  }

  // Fleet listens on every membership through one socket; `channels` only
  // chooses which of them play. A live socket is reused.
  const connect = useCallback(
    async (channelOrChannels: string | string[]) => {
      const desired = normalizeNames(Array.isArray(channelOrChannels) ? channelOrChannels : [channelOrChannels])
      desiredRef.current = desired
      enabledRxRef.current = new Set(desired)
      if (!desired.length || !url) return
      if (connRef.current && !kickedRef.current) return
      openSocket()
    },
    [openSocket, url],
  )

  const disconnect = useCallback(async () => {
    desiredRef.current = []
    closeSocket()
    setState(p => ({ ...p, status: 'disconnected', tx: 'idle', rx: 'idle', readyChannels: [] }))
  }, [closeSocket])

  const disconnectAll = disconnect

  const forceReconnect = useCallback(async () => {
    const desired = desiredRef.current
    closeSocket()
    kickedRef.current = false
    if (desired.length) {
      enabledRxRef.current = new Set(desired)
      openSocket()
    }
  }, [closeSocket, openSocket])

  const setActiveChannel = useCallback((channel: string | null) => {
    const next = (channel || '').trim() || null
    if (activeTxChannelRef.current === next) return
    activeTxChannelRef.current = next
    setState(p => (p.channel === next ? p : { ...p, channel: next || undefined }))
  }, [])

  // A new URL (another org) or language needs a new socket.
  useEffect(() => {
    if (!connRef.current) return
    closeSocket()
    if (url && desiredRef.current.length) openSocket()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, language])

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) void stopTalking()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [stopTalking])

  useEffect(() => () => closeSocket(), [closeSocket])

  const canPTT =
    state.status === 'connected' &&
    state.tx === 'idle' &&
    !!activeTxChannelRef.current &&
    state.readyChannels.includes(activeTxChannelRef.current.toLowerCase())

  return useMemo(
    () => ({
      state,
      connect,
      disconnect,
      disconnectAll,
      setActiveChannel,
      setOnEvent,
      resumeAudio,
      startTalking,
      stopTalking,
      forceReconnect,
      canPTT,
      setStreamVolume,
      isChannelRxEnabled,
      setChannelRxEnabled,
      toggleChannelRxEnabled,
      setRxEnabledChannels,
      getRxEnabledChannels,
    }),
    [
      state,
      connect,
      disconnect,
      disconnectAll,
      setActiveChannel,
      setOnEvent,
      resumeAudio,
      startTalking,
      stopTalking,
      forceReconnect,
      canPTT,
      setStreamVolume,
      isChannelRxEnabled,
      setChannelRxEnabled,
      toggleChannelRxEnabled,
      setRxEnabledChannels,
      getRxEnabledChannels,
    ],
  )
}
